import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useCxcValidation } from "~/hooks/contabilidad/useCxcValidation";
import type { CxcValidationLine } from "~/services/contabilidad/cxcValidationService";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value);
}

function getRowStyle(concepto: string): string {
  if (concepto === "Saldo Final (Calculado)") {
    return "bg-blue-50 font-semibold";
  }
  if (concepto === "Saldo App") {
    return "bg-gray-50";
  }
  if (concepto === "Diferencia") {
    return "bg-yellow-50 font-bold";
  }
  return "";
}

function getSignPrefix(concepto: string): string {
  if (concepto === "Ventas Brutas (Facturas)") return "+";
  if (concepto === "Notas de Credito" || concepto === "Cobranzas") return "-";
  if (concepto === "Saldo Final (Calculado)") return "=";
  return "";
}

export default function ValidacionCxcPage() {
  const navigate = useNavigate();

  // Default: initial date = end of last month, final date = yesterday
  const [initialDate, setInitialDate] = useState(
    dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
  );
  const [finalDate, setFinalDate] = useState(
    dayjs().subtract(1, "day").format("YYYY-MM-DD")
  );

  const {
    data: lines = [],
    isLoading,
    error,
  } = useCxcValidation({
    initialDate,
    finalDate,
  });

  const diferencia = lines.find((l) => l.concepto === "Diferencia");
  const hasDiferencia = diferencia && Math.abs(diferencia.monto) > 100;

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/contabilidad/reportes")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Validacion Mov. CXC</h1>
          <p className="text-sm text-muted-foreground">
            Conciliacion de movimientos de cuentas por cobrar
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Parametros del Reporte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="initial-date" className="text-sm font-medium">
                Saldo Inicial al
              </Label>
              <Input
                id="initial-date"
                type="date"
                value={initialDate}
                onChange={(e) => setInitialDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="final-date" className="text-sm font-medium">
                Saldo Final al
              </Label>
              <Input
                id="final-date"
                type="date"
                value={finalDate}
                onChange={(e) => setFinalDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            {initialDate && finalDate && (
              <div className="text-sm text-muted-foreground">
                Periodo de movimientos:{" "}
                <span className="font-medium">
                  {dayjs(initialDate).add(1, "day").format("DD/MM/YYYY")}
                </span>{" "}
                al{" "}
                <span className="font-medium">
                  {dayjs(finalDate).format("DD/MM/YYYY")}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Reconciliacion CXC</CardTitle>
            {!isLoading && lines.length > 0 && (
              <div className="flex items-center gap-2">
                {hasDiferencia ? (
                  <>
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <span className="text-sm font-medium text-amber-600">
                      Diferencia detectada - Revisar
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-green-600">
                      Cuadrado
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-destructive">
              Error al cargar datos: {(error as Error).message}
            </div>
          ) : lines.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Selecciona las fechas para generar el reporte.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Concepto</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead className="text-right text-muted-foreground">
                    Detalle
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line: CxcValidationLine, index: number) => (
                  <TableRow
                    key={index}
                    className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${getRowStyle(
                      line.concepto
                    )}`}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  >
                    <TableCell className="text-center font-mono text-lg text-muted-foreground">
                      {getSignPrefix(line.concepto)}
                    </TableCell>
                    <TableCell>
                      {line.concepto}
                      {line.detalle?.fecha && (
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({dayjs(line.detalle.fecha).format("DD/MM/YYYY")})
                        </span>
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono ${
                        line.concepto === "Diferencia" && line.monto !== 0
                          ? line.monto > 0
                            ? "text-red-600"
                            : "text-red-600"
                          : ""
                      }`}
                    >
                      {formatCurrency(Math.abs(line.monto))}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {line.detalle?.cantidad !== undefined && (
                        <span>{line.detalle.cantidad} docs</span>
                      )}
                      {line.detalle?.formula && (
                        <span className="font-mono text-xs">
                          {line.detalle.formula}
                        </span>
                      )}
                      {line.detalle?.alerta && (
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            line.detalle.alerta === "REVISAR"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {line.detalle.alerta}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            <strong>Formula:</strong> Saldo Final = Saldo Inicial + Ventas -
            Notas de Credito - Cobranzas
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            * Solo incluye facturas con numero BSale (verificadas SII). Las
            cobranzas se filtran por fecha de verificacion.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
